import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { tokenAiRepository } from '../../../lib/repositories/token-ai.repository';
import { useToast } from '../../../lib/toast';
import { useAuthStore } from '../../../stores/auth';
import { VAI_TRO_QUAN_TRI_TENANT } from '../../auth/nghiep-vu/quyen-cong-tenant';
import { thuLaiToken } from '../../tenant/hooks/useHanMucTokenAi';
import type { AiLimit, AiLimitCreate, AiLimitUpdate, DongPhanRa, PhamViHanMucAi, SuDungNgay, TheoPhanRa } from '../types';

export const KHOA_MAN_TOKEN_AI = {
  tatCa: ['token-ai'] as const,
  ngay: (t: string, from: string, to: string) => ['token-ai', 'ngay', t, from, to] as const,
  phanRa: (t: string, thang: string, theo: TheoPhanRa) => ['token-ai', 'phan-ra', t, thang, theo] as const,
  hanMuc: (t: string) => ['token-ai', 'han-muc', t] as const,
  hanMucLoc: (t: string, phamVi?: PhamViHanMucAi) => ['token-ai', 'han-muc', t, phamVi ?? 'tat-ca'] as const,
};

/**
 * Quản trị tổ chức: role lớp 1 `TENANT_ADMIN` khi đăng nhập thật; tài khoản mẫu không có role lớp 1 nên đọc theo nhãn
 * vai trò. API chỉ mở cho `TENANT_ADMIN` (ADR-0088 mục 3).
 */
export function useLaQuanTriToChuc(): boolean {
  return useAuthStore((s) => {
    const u = s.user;
    if (!u) return false;
    return u.vaiTroLop1 ? u.vaiTroLop1.includes(VAI_TRO_QUAN_TRI_TENANT) : u.roles.includes('vaiTro.quanTriToChuc');
  });
}

export function useSuDungTheoNgay(tenantId: string, from: string, to: string) {
  return useQuery<SuDungNgay[], ApiError>({
    queryKey: KHOA_MAN_TOKEN_AI.ngay(tenantId, from, to),
    queryFn: () => tokenAiRepository.theoNgay(tenantId, from, to),
    staleTime: 60_000,
    retry: thuLaiToken,
  });
}

export function usePhanRa(tenantId: string, thang: string, theo: TheoPhanRa) {
  return useQuery<DongPhanRa[], ApiError>({
    queryKey: KHOA_MAN_TOKEN_AI.phanRa(tenantId, thang, theo),
    queryFn: () => tokenAiRepository.phanRa(tenantId, thang, theo),
    staleTime: 60_000,
    retry: thuLaiToken,
  });
}

export function useHanMucAi(tenantId: string, phamVi?: PhamViHanMucAi) {
  return useQuery<AiLimit[], ApiError>({
    queryKey: KHOA_MAN_TOKEN_AI.hanMucLoc(tenantId, phamVi),
    queryFn: () => tokenAiRepository.danhSachHanMuc(tenantId, phamVi),
    staleTime: 30_000,
    retry: thuLaiToken,
  });
}

function useLamMoiHanMuc(tenantId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KHOA_MAN_TOKEN_AI.hanMuc(tenantId) });
}

/** Lỗi của ba thao tác dưới không toast: hộp giữ dữ liệu đã nhập và hiện lỗi cạnh ô. */
export function useTaoHanMucAi(tenantId: string) {
  const lamMoi = useLamMoiHanMuc(tenantId);
  const toast = useToast();
  const { t } = useT();
  return useMutation<AiLimit, ApiError, AiLimitCreate>({
    mutationFn: (du) => tokenAiRepository.taoHanMuc(tenantId, du),
    onSuccess: () => {
      void lamMoi();
      toast.success(t('manTokenAi.daTao'));
    },
  });
}

export function useSuaHanMucAi(tenantId: string) {
  const lamMoi = useLamMoiHanMuc(tenantId);
  const toast = useToast();
  const { t } = useT();
  return useMutation<AiLimit, ApiError, { id: string; du: AiLimitUpdate }>({
    mutationFn: ({ id, du }) => tokenAiRepository.suaHanMuc(tenantId, id, du),
    onSuccess: () => {
      void lamMoi();
      toast.success(t('manTokenAi.daLuu'));
    },
  });
}

export function useXoaHanMucAi(tenantId: string) {
  const lamMoi = useLamMoiHanMuc(tenantId);
  const toast = useToast();
  const { t } = useT();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => tokenAiRepository.xoaHanMuc(tenantId, id),
    onSuccess: () => {
      void lamMoi();
      toast.success(t('manTokenAi.daXoa'));
    },
  });
}

/** Công tắc trong bảng không có hộp để hiện lỗi: lỗi đi toast, câu của backend giữ nguyên. */
export function useBatHanMucAi(tenantId: string) {
  const lamMoi = useLamMoiHanMuc(tenantId);
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation<AiLimit, ApiError, { id: string; bat: boolean }>({
    mutationFn: ({ id, bat }) => tokenAiRepository.suaHanMuc(tenantId, id, { bat }),
    onSuccess: (_kq, { bat }) => {
      void lamMoi();
      toast.success(t(bat ? 'manTokenAi.daBat' : 'manTokenAi.daTat'));
    },
    onError: (e) => toast.error(dichLoi(e)),
  });
}
